import MainTable, { MainTableColumn } from '@/components/company/table/MainTable';
import type { IJobDescriptionPeran } from '@/types/job-description-peran.types';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { ORG_DIVISIONS, ORG_LEVELS } from '@/utils/constants/organization-matrix';

interface Props {
  isLoading: boolean;
  data: {
    content: IJobDescriptionPeran[];
    totalElements: number;
    totalPages: number;
  };
  uploadedCounts: Record<number, number>;
  pageConfig: DataConfig;
  setPageConfig: React.Dispatch<React.SetStateAction<DataConfig>>;
  onDetail: (row: IJobDescriptionPeran) => void;
}

const labelOf = (list: readonly { key: string; label: string }[], key?: string) =>
  list.find(item => item.key === key)?.label ?? key ?? '—';

export default function ReportPeranTable({
  isLoading,
  data,
  uploadedCounts,
  pageConfig,
  setPageConfig,
  onDetail,
}: Props) {
  const columns: MainTableColumn[] = [
    {
      name: 'name',
      label: 'Nama Jabatan',
      sortKey: 'org_level',
      render: (_: unknown, row: IJobDescriptionPeran) => (
        <span className="font-semibold text-gray-800 dark:text-gray-100">{row.name}</span>
      ),
    },
    {
      name: 'division_key',
      label: 'Departemen',
      sortable: false,
      render: (_: unknown, row: IJobDescriptionPeran) => (
        <span className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
          {labelOf(ORG_DIVISIONS, row.division_key)}
        </span>
      ),
    },
    {
      name: 'level_key',
      label: 'Level',
      sortable: false,
      render: (_: unknown, row: IJobDescriptionPeran) => (
        <span className="text-sm text-gray-700 dark:text-gray-300">{labelOf(ORG_LEVELS, row.level_key)}</span>
      ),
    },
    {
      name: 'job_description_count',
      label: 'Jumlah Laporan',
      sortKey: 'job_description_count',
      headerClassName: 'text-center',
      render: (_: unknown, row: IJobDescriptionPeran) => (
        <div className="flex justify-center">
          <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            {row.job_description_count} Uraian
          </span>
        </div>
      ),
    },
    {
      name: 'uploaded_files',
      label: 'File Ter-upload',
      sortable: false,
      headerClassName: 'text-center',
      render: (_: unknown, row: IJobDescriptionPeran) => (
        <div className="flex justify-center">
          <span className="text-sm text-gray-700 dark:text-gray-300">{uploadedCounts[row.id] ?? 0} File</span>
        </div>
      ),
    },
    {
      name: 'aksi',
      label: 'Aksi',
      sortable: false,
      headerClassName: 'text-center',
      render: (_: unknown, row: IJobDescriptionPeran) => (
        <div className="flex justify-center items-center">
          <button
            type="button"
            aria-label={`Kelola ${row.name}`}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            onClick={() => onDetail(row)}
          >
            Kelola
          </button>
        </div>
      ),
    },
  ];

  return (
    <MainTable
      showHeader={false}
      isLoading={isLoading}
      data={{
        content: data.content as unknown as { [key: string]: unknown }[],
        totalElements: data.totalElements,
        totalPages: data.totalPages,
      }}
      columns={columns}
      pageConfig={pageConfig}
      setPageConfig={setPageConfig}
      showActions={false}
      sectionClassName="px-5"
    />
  );
}
